/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ArchitectureTypes.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  DialogContentText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";

import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";

import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";

import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";

import ZoneDetails from "./ZoneDetails";
import DeliveryMarket from "./DeliveryMarket";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import profileActions from "../../redux/actions/profile";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CommonInputSwitch from "../../components/customComponentsV2/common/InputSwitch/CommonInputSwitch";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import carForVLC from "../../assets/carForVLC.png";
import * as XLSX from "xlsx";

function ArchitectureTypes({ open, architectureDetails, zoneDetails }) {
  const adminSettingsReducer = useSelector(
    (state) => state.adminSettingsReducer,
  );
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  const [searchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deliveryMarketCount, setDeliveryMarketCount] = useState(0);
  const [deliveryMarketExportData, setDeliveryMarketExportData] = useState([]);

  useEffect(() => {
  }, [open]);

  useEffect(() => {
    const mappedData = architectureDetails?.map((item, index) => {
      return {
        ...item,
        sno: (page - 1) * rowsPerPage + index + 1,
        id: item?.id || index,
        name: item?.name,
        is_zones_available: item?.is_zones_available ? "Yes" : "No",
        created_at: convertUtcToTimeZone(
          item?.created_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        updated_at: convertUtcToTimeZone(
          item?.updated_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        allData: item,
      };
    });
    setTableRowsData(mappedData || []);

    const deliveryMarkets =
      adminSettingsReducer?.adminSettings?.data?.product_delivery_markets || [];
    setDeliveryMarketCount(deliveryMarkets.length);
  }, [
    architectureDetails,
    zoneDetails,
    adminSettingsReducer,
    page,
    rowsPerPage,
    timeZoneDetails,
  ]);

  const handleEditClick = useCallback((params) => {
    setSelectedRow(params.row);
    setOpenEditDialog(true);
  }, []);

  const COLUMNS = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
      },
      {
        field: "name",
        headerName: "Architecture Name",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "is_zones_available",
        headerName: "Zones Available",
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: "created_at",
        headerName: "Created",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "updated_at",
        headerName: "Updated",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.4,
        minWidth: 90,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params);
              }}
            >
              <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleEditClick],
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery) return tableRowsData;
    return tableRowsData.filter((row) =>
      COLUMNS.some((column) =>
        row[column.field]
          ?.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      ),
    );
  }, [searchQuery, tableRowsData, COLUMNS]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleExportToExcel = () => {
    if (activeTab === 1) {
      if (!deliveryMarketExportData || deliveryMarketExportData.length === 0) {
        console.warn("No data to export");
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(deliveryMarketExportData);
      XLSX.utils.book_append_sheet(wb, ws, "Delivery Markets");
      XLSX.writeFile(wb, "Delivery_Markets.xlsx");
    }
  };

  const handleAddClick = () => {
    setOpenAddDialog(true);
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Architecture & Delivery Market" },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box>
        <CustomTabsV2
          headingCentered={false}
          onTabChange={setActiveTab}
          tabs={[
            {
              name: "Architecture Type",
              count: filteredRows?.length || 0,
              content: (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: {
                        xs: "0 8px 2px 0px",
                        md: "0 12px 4px 0px",
                      },
                      mt: -1.5,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          fontStyle: "Open Sans",
                          color: "#53565A",
                        }}
                      >
                        Type of Architecture
                      </Typography>
                    </Box>
                    <Box>
                      <AddButtonV2 handleOnClick={handleAddClick} />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: "calc(100vh - 180px)",
                      width: "100%",
                      overflowY: "auto",
                      padding: { xs: "8px", md: "16px" },
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "#a8a8a8",
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        width: "100%",
                        maxHeight: "100%",
                        overflowY: "auto",
                        padding: { xs: "8px", md: "16px" },
                        "&::-webkit-scrollbar": {
                          width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#c1c1c1",
                          borderRadius: "4px",
                          "&:hover": {
                            backgroundColor: "#a8a8a8",
                          },
                        },
                      }}
                    >
                      <Box>
                        {filteredRows.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              display: "flex",
                              border: "1px solid #BBBCBC",
                              borderRadius: "0px",
                              mb: 1,
                              backgroundColor: "#FFFFFF",
                              overflow: "hidden",
                              minHeight: "200px",
                            }}
                          >
                            <Box
                              sx={{
                                width: "25%",
                                minWidth: "150px",
                                borderRight: "1px solid #BBBCBC",
                                backgroundColor: "#F9FAFB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                padding: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={carForVLC}
                                  alt={item.name}
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "180px",
                                    objectFit: "contain",
                                  }}
                                />
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                flex: 1,
                                padding: 2,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >

                              <Box sx={{ display: "flex" }}>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    fontStyle: "Open Sans",
                                    color: "#000000",
                                    mb: 2,
                                    maxWidth: "100%",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    textAlign: "justify",
                                  }}
                                >
                                  {item?.name
                                    ? item.name.charAt(0).toUpperCase() +
                                      item.name.slice(1).toLowerCase()
                                    : ""}{" "}
                                  Architecture
                                </Typography>
                              </Box>

                              <Box sx={{ display: "flex" }}>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontStyle: "Open Sans",
                                    color: "#000000",
                                  }}
                                >
                                  Description:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontStyle: "Open Sans",
                                    color: "#000000",
                                    lineHeight: 1.8,
                                    ml: 0.5,
                                    maxWidth: "100%",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    whiteSpace: "pre-wrap",
                                    textAlign: "justify",
                                  }}
                                >
                                  {item?.allData?.description ||
                                    "No description available"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}

                        {filteredRows.length === 0 && (
                          <Box
                            sx={{
                              textAlign: "center",
                              py: 8,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "16px",
                                color: "#9CA3AF",
                                fontFamily: "Open Sans",
                              }}
                            >
                              No architecture types found
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ mt: 2, bgcolor: "#FFFFFF", p: 2 }}>
                        <ZoneDetails zoneDetails={zoneDetails} />
                      </Box>
                    </Box>
                  </Box>
                </>
              ),
            },
            {
              name: "Delivery Market",
              count: deliveryMarketCount,
              content: (
                <Box>
                  <DeliveryMarket
                    onExportDataReady={setDeliveryMarketExportData}
                    handleExportToExcel={handleExportToExcel}
                  />
                </Box>
              ),
            },
          ]}
        />
      </Box>

      <AddArchitectureType
        open={openAddDialog}
        setOpenAddDialog={setOpenAddDialog}
      />

      <EditArchitectureType
        open={openEditDialog}
        setOpenEditDialog={setOpenEditDialog}
        selectedRow={selectedRow}
      />

      <DeleteArchitectureType
        open={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        selectedRow={selectedRow}
      />
    </Box>
  );
}

export default ArchitectureTypes;

const AddArchitectureType = ({ open, setOpenAddDialog }) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isZonesAvailable, setIsZonesAvailable] = useState(false);

  const handleClose = () => {
    setOpenAddDialog(false);
    setName("");
    setDescription("");
    setIsZonesAvailable(false);
  };

  const handleAdd = () => {
    const reqObj = {
      name: name,
      description: description,
      is_zones_available: isZonesAvailable,
      current_time: new Date().toISOString(),
    };

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.add_architecture_types,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_admin_settings,
          show_toast: false,
        }),
      );
    });

    handleClose();
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Add Architecture"}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      onClickSuccess={handleAdd}
      onDisabled={
        !(
          name?.length > 2 &&
          name?.trim() !== "" &&
          description?.length > 2 &&
          description?.trim() !== ""
        )
      }
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={1} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Name"
          value={name}
          setValue={(val) => {
            if (val.length <= 35) {
              setName(val);
            }
          }}
          required={true}
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label="Description"
          value={description}
          setValue={(val) => {
            if (val.length <= 100) {
              setDescription(val);
            }
          }}
          required={true}
          maxLength={100}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CommonInputSwitch
            checked={isZonesAvailable || false}
            onChange={setIsZonesAvailable}
            label="Zone Available"
          />
          <Typography
            sx={{
              fontFamily: "Open Sans",
              fontSize: "14px",
              minWidth: "30px",
            }}
          >
            {isZonesAvailable ? "Yes" : "No"}
          </Typography>
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
};

const EditArchitectureType = ({ open, setOpenEditDialog, selectedRow }) => {
  const dispatch = useDispatch();

  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editIsZonesAvailable, setEditIsZonesAvailable] = useState(false);

  useEffect(() => {
    if (selectedRow) {
      setEditName(selectedRow?.name || "");
      setEditDescription(selectedRow?.allData?.description || "");
      setEditIsZonesAvailable(
        selectedRow?.allData?.is_zones_available || false,
      );
    }
  }, [selectedRow]);

  const handleClose = () => {
    setOpenEditDialog(false);
  };

  const handleEdit = () => {
    const reqObj = {
      id: selectedRow?.allData?.id,
      name: editName,
      description: editDescription,
      is_zones_available: editIsZonesAvailable,
      current_time: new Date().toISOString(),
    };

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.update_architecture_types,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_admin_settings,
          show_toast: false,
        }),
      );
    });

    handleClose();
  };

  const handleUploadFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("profile_pic", file);

      dispatch(
        HandleApiActions({
          ...profileActions.upload_profile_pic,
          params: formData,
          show_toast: true,
        }),
      ).then((resp) => {
        if (!resp?.error) {
          dispatch(
            HandleApiActions({
              ...profileActions.get_profile_details,
            }),
          );
        }
      });
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"UPDATE ARCHITECTURE"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleEdit}
      onDisabled={
        !(
          editName?.length > 2 &&
          editName?.trim() !== "" &&
          editDescription?.length > 2 &&
          editDescription?.trim() !== ""
        )
      }
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={1}>
        <CustomFormTextFieldV2
          label="Name"
          value={editName}
          setValue={setEditName}
          txtFldStyle={{ width: "270px" }}
          required={true}
          disabled={true}
        />
        <CustomFormTextFieldV2
          label="Description"
          value={editDescription}
          setValue={setEditDescription}
          txtFldStyle={{ width: "270px" }}
          required={true}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CommonInputSwitch
            checked={editIsZonesAvailable || false}
            onChange={setEditIsZonesAvailable}
            label="Zone Available"
          />
          <Typography
            sx={{
              fontFamily: "Open Sans",
              fontSize: "14px",
              minWidth: "30px",
            }}
          >
            {editIsZonesAvailable ? "YES" : "NO"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <Typography>
            Upload files<span style={{ color: "red" }}>*</span>
          </Typography>
          <Button
            size="small"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
            component="label"
            endIcon={<UploadFileIcon color="blue" size={18} />}
          >
            <input accept="image/*" type="file" onChange={handleUploadFile} />
          </Button>
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
};

const DeleteArchitectureType = ({ open, setOpenDeleteDialog, selectedRow }) => {
  const dispatch = useDispatch();

  const handleDelete = () => {
    const reqObj = {
      id: selectedRow?.allData?.id,
      current_time: new Date().toISOString(),
    };

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.delete_architecture_types,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_admin_settings,
          show_toast: false,
        }),
      );
    });

    setOpenDeleteDialog(false);
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={() => setOpenDeleteDialog(false)}
      title={"DELETE"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeleteDialog(false)}
      actionButtonStyle={"continuous"}
    >
      <DialogContentText>Are you sure you want to delete?</DialogContentText>
    </CustomDialogBoxV2>
  );
};

const architectureRowShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  description: PropTypes.string,
  is_zones_available: PropTypes.bool,
  created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updated_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const selectedRowShape = PropTypes.shape({
  name: PropTypes.string,
  allData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    is_zones_available: PropTypes.bool,
  }),
});

ArchitectureTypes.propTypes = {
  open: PropTypes.bool,
  architectureDetails: PropTypes.arrayOf(architectureRowShape),
  zoneDetails: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

AddArchitectureType.propTypes = {
  open: PropTypes.bool,
  setOpenAddDialog: PropTypes.func.isRequired,
};

EditArchitectureType.propTypes = {
  open: PropTypes.bool,
  setOpenEditDialog: PropTypes.func.isRequired,
  selectedRow: selectedRowShape,
};

DeleteArchitectureType.propTypes = {
  open: PropTypes.bool,
  setOpenDeleteDialog: PropTypes.func.isRequired,
  selectedRow: selectedRowShape,
};
